<template>
  <NiceModal :name="name" :title="$gettext('Checkin guest')" @close="cancel" isForm @submit="submit">
    <section v-if="guest">
      <!-- Name -->
      <NiceValue :title="$gettext('Name')">
        <h2 class="p-0 m-0">{{ guestName }}</h2>
      </NiceValue>

      <!-- Group -->
      <NiceValue :title="$gettext('Group')" :value="guest.group.name">
        <span class="badge" :style="{ 'background-color': guest.group.color }">{{ guest.group.name }}</span>
      </NiceValue>

      <!-- Checked timestamp -->
      <NiceValue :title="$gettext('Checked timestamp')" v-if="guest.checkin_timestamp">
        <span>{{ formatDateWithTime(guest.checkin_timestamp, true, true) }}</span>
      </NiceValue>

      <!-- Checked in by -->
      <NiceValue :title="$gettext('Checked in by')" v-if="guest.checked_in_by">
        <span>{{ guest.checked_in_by.first_name }} {{ guest.checked_in_by.last_name }}</span>
      </NiceValue>

      <!-- Checked in plus one count -->
      <NiceValue
        :title="$gettext('Checked in plus one count')"
        v-if="modules.PLUS_ONES && guest.is_checked_in && guest.checked_in_plus_one_count != undefined"
        :value="guest.checked_in_plus_one_count"
      />

      <!-- Partner -->
      <NiceValue :title="$gettext('Partner')" v-if="guest.partner" :value="guest.partner.name" />

      <!-- Company name -->
      <NiceValue :title="$gettext('Company name')" v-if="guest.company_name" :value="guest.company_name" />

      <!-- Plus one -->
      <NiceInput
        :title="
          $gettext('Plus one') +
          ' (' +
          guest.checked_in_plus_one_count +
          '/' +
          guest.plus_one_count +
          ') Max: ' +
          (guest.plus_one_count - guest.checked_in_plus_one_count)
        "
        v-if="
          modules.PLUS_ONES && guest.group.allow_plus_one && guest.plus_one_count > 0 && guest.plus_one_count - guest.checked_in_plus_one_count > 0
        "
        v-model="form.plus_one_count"
        type="number"
        placeholder="0"
        :min="0"
        :max="guest.plus_one_count - guest.checked_in_plus_one_count"
        :step="1"
        :required="true"
      />

      <!-- Loading -->
      <NiceLoading v-if="loading" fullDiv></NiceLoading>

      <!-- <pre>{{ guest }}</pre> -->
    </section>

    <template #footer>
      <!-- Cancel -->
      <NiceButton :text="$gettext('Cancel')" @click="cancel" type="default" plain icon="icon-x" />

      <!-- Submit -->
      <NiceButton
        :text="$gettext('Checkin')"
        nativeType="submit"
        :loading="loading"
        :disabled="(guest.is_checked_in && guest.plus_one_count - guest.checked_in_plus_one_count <= 0) || guest._checkin_expired"
        icon="icon-check"
      />
    </template>
  </NiceModal>
</template>

<script setup>
import { defineEmits, defineProps, inject, computed, ref, onMounted, watch } from "vue";

import ApiService from "@/services/api/api.service";
import CommonService from "@/services/common.service";
import ErrorService from "@/services/error.service";
import NiceValue from "@/components/NiceValue.vue";

const nice = inject("nice");

import { useRouter } from "vue-router";
const router = useRouter();

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

import { useGeneralStore } from "@/store/general.store";
import { formatDateWithTime } from "@/plugins/date";
const generalStore = useGeneralStore();
const modules = computed(() => generalStore.modules);

const emit = defineEmits(["cancel", "success"]);
const props = defineProps({
  guest: {
    type: Object,
    default: () => null,
  },
});

const name = "checkin-guest";
const loading = ref(false);
const form = ref({
  plus_one_count: 0,
});
const guestName = computed(() => (props.guest ? props.guest.first_name + " " + props.guest.last_name : ""));
const organizationId = router.currentRoute.value.params.organizationId;
const eventId = router.currentRoute.value.params.eventId;

function close() {
  nice.modal(name, false);
}

function cancel() {
  emit("cancel");
  close();
}

async function submit() {
  loading.value = true;
  try {
    await ApiService.guests.checkin(organizationId, eventId, props.guest.id, form.value);
    CommonService.success($gettext("Guest '%{name}' was checked in", { name: guestName.value }));
    emit("success");
    close();
  } catch (e) {
    ErrorService.showError(e);
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.guest,
  (newVal) => {
    if (newVal) {
      form.value.plus_one_count = newVal.plus_one_count - newVal.checked_in_plus_one_count;
    }
  }
);

onMounted(() => {
  form.value.plus_one_count = 0;
});
</script>
